import { Mail } from "lucide-react";
import {
  GOOGLE_BUSINESS_URL,
  TRUSTPILOT_BUSINESS_URL,
} from "@/content/google-reviews";
import { FOCUS_RING } from "@/lib/lazy-route";
import { cn } from "@/lib/utils";

export type ReviewSource = "google" | "trustpilot" | "email";

function GoogleMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" className={className} aria-hidden="true" focusable="false">
      <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z" />
      <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z" />
      <path fill="#FBBC05" d="M11.69 28.18A13.5 13.5 0 0 1 10.98 24c0-1.45.25-2.86.71-4.18v-5.7H4.34A22 22 0 0 0 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z" />
      <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z" />
    </svg>
  );
}

function TrustpilotMark({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" focusable="false">
      <rect width="24" height="24" rx="3" fill="#00B67A" />
      <path
        fill="#fff"
        d="M12 4.6l1.94 4.28 4.66.45-3.5 3.12 1.01 4.58L12 14.6l-4.11 2.43 1.01-4.58-3.5-3.12 4.66-.45L12 4.6z"
      />
    </svg>
  );
}

const SOURCE_LABEL: Record<ReviewSource, string> = {
  google: "Google Review",
  trustpilot: "Trustpilot Review",
  email: "Received By Email",
};

/**
 * Small provenance badge shown on every curated review card so the origin of
 * the review is always visible (Google, Trustpilot, or sent to us by email).
 */
export function ReviewSourceBadge({
  source,
  className,
}: {
  source?: ReviewSource;
  className?: string;
}) {
  if (!source) return null;
  return (
    <span
      className={cn(
        "inline-flex w-fit items-center gap-1.5 rounded-full border border-border bg-secondary/60 px-2.5 py-1 text-[11px] font-semibold leading-none text-muted-foreground",
        className,
      )}
    >
      {source === "google" ? (
        <GoogleMark className="h-3.5 w-3.5" />
      ) : source === "trustpilot" ? (
        <TrustpilotMark className="h-3.5 w-3.5" />
      ) : (
        <Mail className="h-3.5 w-3.5 text-accent" aria-hidden="true" />
      )}
      {SOURCE_LABEL[source]}
    </span>
  );
}

/**
 * Section-level statement of where the reviews above came from, with links to
 * the two public review profiles.
 */
export function ReviewProvenanceNote({ className }: { className?: string }) {
  return (
    <p
      className={cn(
        "text-center text-xs leading-relaxed text-muted-foreground md:text-sm",
        className,
      )}
    >
      Every review above is published on our public Google or Trustpilot profile, or was sent to
      us directly by the customer, as marked on each card.{" "}
      <a
        href={GOOGLE_BUSINESS_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={cn("font-semibold text-foreground underline underline-offset-4 hover:text-accent", FOCUS_RING)}
      >
        View On Google
      </a>{" "}
      or{" "}
      <a
        href={TRUSTPILOT_BUSINESS_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={cn("font-semibold text-foreground underline underline-offset-4 hover:text-accent", FOCUS_RING)}
      >
        View On Trustpilot
      </a>
      .
    </p>
  );
}
